import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  suppressErrors?: boolean;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.log('ErrorBoundary caught an error:', error, errorInfo);
    
    // Check if this is a Firefox-specific input error that we want to suppress
    const isFirefoxInputError = 
      error.message?.includes('Script error') ||
      error.message?.includes('handleError') ||
      error.stack?.includes('handleError') ||
      (navigator.userAgent.toLowerCase().includes('firefox') && 
       (error.message?.includes('input') || error.message?.includes('change')));
    
    if (isFirefoxInputError && this.props.suppressErrors) {
      console.log('Suppressing Firefox input error in development mode');
      // Reset the error state to prevent showing the error UI
      this.setState({ hasError: false, error: null });
      return;
    }
    
    // For other errors, log them but don't suppress
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError && !this.props.suppressErrors) {
      // You can render any custom fallback UI
      return this.props.fallback || (
        <div className="error-boundary">
          <h2>Something went wrong.</h2>
          <p>Error: {this.state.error?.message}</p>
        </div>
      );
    }

    return this.props.children;
  }
}
